import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';

const Footer = () => {
  const practiceAreas = [
    { name: 'Maritime Injuries', path: '/maritime-injuries' },
    { name: 'Car Crashes', path: '/car-crashes' },
    { name: 'First Responder Crashes', path: '/first-responder-crashes' },
    { name: 'Motorcycle Crashes', path: '/motorcycle-crashes' },
    { name: 'Slip and Falls', path: '/slip-and-falls' },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Amy', path: '/about-amy' },
    { name: 'Is Amy Right For You?', path: '/is-amy-right-for-you' },
    { name: 'Learning Center', path: '/blog' },
    { name: 'Legal Glossary', path: '/legal-glossary' },
    { name: 'Areas I Serve', path: '/areas-we-serve' },
    { name: 'Pricing & Costs', path: '/pricing' },
    { name: 'Compensation Estimator', path: '/compensation-estimator' },
    { name: 'FAQs', path: '/faqs' },
    { name: 'Contact', path: '/contact' },
  ];

  // Car Accident Resources — focused hub for users researching car crash claims.
  const carAccidentResources = [
    { name: 'Car Crashes Overview', path: '/car-crashes' },
    { name: 'How Insurance Companies Handle Claims', path: '/how-insurance-companies-handle-claims' },
    { name: 'Rear-End Accidents', path: '/rear-end-accident-lawyer' },
    { name: 'Everett Car Accident Lawyer', path: '/everett-car-accident-lawyer' },
    { name: 'Case Calculator', path: '/compensation-estimator' },
    { name: 'How Much Is My Case Worth?', path: '/how-much-is-my-case-worth' },
  ];

  return (
    <footer className="bg-secondary text-white" data-testid="footer" role="contentinfo" aria-label="Site footer">
      {/* Main Footer */}
      <div className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-10 lg:gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-1 xl:col-span-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_fbb64d56-36dd-410e-80a1-5a26f6e86efa/artifacts/sh975zrq_ab_logo_white.png"
                alt="Amy C Brown Law"
                loading="lazy"
                className="h-20 w-auto mb-6"
              />
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Compassionate, direct legal representation for personal injury victims across Washington State and Alaska.
              </p>
              <address className="not-italic space-y-3">
                <a 
                  href="tel:425-675-1224" 
                  className="flex items-center gap-3 text-slate-300 hover:text-teal-400 transition-colors"
                  aria-label="Call us at 425-675-1224"
                >
                  <Phone className="w-4 h-4 text-teal-400" aria-hidden="true" />
                  (425) 675-1224
                </a>
                <a 
                  href="mailto:amy@amycbrownlaw.com" 
                  className="flex items-center gap-3 text-slate-300 hover:text-teal-400 transition-colors"
                  aria-label="Email amy@amycbrownlaw.com"
                >
                  <Mail className="w-4 h-4 text-teal-400" aria-hidden="true" />
                  amy@amycbrownlaw.com
                </a>
                <div className="flex items-start gap-3 text-slate-300">
                  <MapPin className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" aria-hidden="true" />
                  <span>Everett, Washington<br />Serving WA State & Alaska</span>
                </div>
              </address>
            </div>

            {/* Practice Areas */}
            <nav aria-label="Practice Areas">
              <h3 className="font-heading text-lg font-semibold mb-6 text-white">Practice Areas</h3>
              <ul className="space-y-3" role="list">
                {practiceAreas.map((area) => (
                  <li key={area.path}>
                    <Link 
                      to={area.path}
                      className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                    >
                      {area.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Quick Links */}
            <nav aria-label="Quick Links">
              <h3 className="font-heading text-lg font-semibold mb-6 text-white">Quick Links</h3>
              <ul className="space-y-3" role="list">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Local Service Areas */}
            <nav aria-label="Local Service Areas">              <h3 className="font-heading text-lg font-semibold mb-6 text-white">Local Pages</h3>
              <ul className="space-y-3" role="list">
                <li>
                  <Link 
                    to="/everett-slip-and-fall-lawyer"
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                  >
                    Everett Slip & Fall
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/tacoma-slip-and-fall-lawyer"
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                  >
                    Tacoma Slip & Fall
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/bellingham-slip-and-fall-lawyer"
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                  >
                    Bellingham Slip & Fall
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/areas-we-serve"
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                  >
                    All Areas I Serve →
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Car Accident Resources */}
            <nav aria-label="Car Accident Resources" data-testid="footer-car-accident-resources">
              <h3 className="font-heading text-lg font-semibold mb-6 text-white">Car Accident Resources</h3>
              <ul className="space-y-3" role="list">
                {carAccidentResources.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className="text-slate-400 hover:text-teal-400 transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Awards Section */}
      <div className="border-t border-slate-700/50 py-8">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <p className="text-slate-400 text-sm font-medium">Awards & Recognition:</p>
            <div className="flex items-center gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/aecnwanu_10Best.png"
                alt="10 Best Attorney 2025 - Client Satisfaction"
                loading="lazy"
                className="h-16 w-auto"
                data-testid="footer-award-10best"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/2s3q26fe_top10.webp"
                alt="Top 10 Personal Injury Attorney 2025"
                loading="lazy"
                className="h-16 w-auto"
                data-testid="footer-award-top10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-700/50 py-6">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Amy C. Brown Law. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-slate-500 text-sm">
              <Link to="/privacy-policy" className="hover:text-teal-400 transition-colors">
                Privacy Policy
              </Link>
              <span>|</span>
              <Link to="/terms-of-service" className="hover:text-teal-400 transition-colors">
                Terms of Service
              </Link>
              <span>|</span>
              <Link to="/sitemap" className="hover:text-teal-400 transition-colors">
                Sitemap
              </Link>
            </div>
          </div>
          {/* External Profile Links for AI Trust Signals */}
          <div className="mt-4 pt-4 border-t border-slate-700/30 flex flex-wrap justify-center gap-4 text-xs text-slate-500">
            <a href="https://www.mywsba.org/PersonifyEbusiness/LegalDirectory/LegalProfile.aspx?Usr_ID=000000047430" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors" data-testid="footer-wsba-link">
              WSBA Member
            </a>
            <span>|</span>
            <a href="https://www.youtube.com/@AmyCBrownLaw" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
              YouTube
            </a>
            <span>|</span>
            <a href="https://www.linkedin.com/company/amy-c-brown-law-pllc/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
              LinkedIn
            </a>
            <span>|</span>
            <a href="https://www.avvo.com/attorneys/98201-wa-amy-brown-4585008.html" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
              Avvo Profile
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
